// src/components/game/InterrogationScene.jsx
import React, { useState, useEffect, useRef } from 'react';

export default function InterrogationScene({ eventData, onChoiceMade }) {
  const [showOptions, setShowOptions] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const videoRef = useRef(null);

  // --- 1. SMART VIDEO SELECTION ---
  const videoSrc = eventData.videoSrc || '/assets/npcs/investigator_idle.mp4';

  useEffect(() => {
    setShowOptions(false);
    const timer = setTimeout(() => setShowOptions(true), 1500);
    
    // Force play attempt
    if (videoRef.current) {
        videoRef.current.play().catch(e => console.log("Autoplay blocked:", e));
    }
    
    return () => clearTimeout(timer);
  }, [eventData]);

  if (!eventData) return <div className="bg-black text-white p-10 h-full flex items-center justify-center">Loading Interrogation Data...</div>;

  return (
    <div className="relative w-full h-full bg-black overflow-hidden flex flex-col items-center justify-center">
      
      {/* --- VIDEO LAYER --- */}
      {!videoError ? (
        <video
          ref={videoRef}
          src={videoSrc} 
          autoPlay
          loop
          muted
          playsInline
          className="absolute top-0 left-0 w-full h-full object-cover opacity-80"
          onError={(e) => {
              console.error("NPC Video Failed:", videoSrc);
              setVideoError(true); // Switch to fallback mode
          }}
        />
      ) : (
        // FALLBACK IF VIDEO FAILS
        <div className="absolute inset-0 bg-gray-900 flex items-center justify-center">
            <div className="text-center p-4">
                <div className="text-6xl mb-4">🕵️‍♂️</div>
                <p className="text-red-400 text-xs uppercase tracking-widest">Video Signal Lost</p>
                <p className="text-gray-500 text-xs">Proceeding with Audio Only</p>
            </div>
        </div>
      )}
      
      {/* VIGNETTE OVERLAY */}
      <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-black pointer-events-none"></div>

      {/* --- INTERFACE LAYER --- */}
      <div className="relative z-10 w-full max-w-4xl p-6 mt-auto mb-10">
        <div className="mb-4">
          <span className="bg-red-700 text-white px-4 py-1 rounded text-sm font-bold uppercase tracking-widest shadow-lg">
            {eventData.npc || "Interrogator"}
          </span>
        </div>

        <div className="bg-gray-900/90 border-l-4 border-red-500 p-6 rounded-r-lg shadow-2xl backdrop-blur-sm mb-6">
          <p className="text-xl md:text-2xl text-white font-serif leading-relaxed">
            "{eventData.dialogue || eventData.question || "..."}"
          </p>
        </div>

        {/* OPTIONS (Always show after delay) */}
        {showOptions && eventData.options && (
          <div className="grid grid-cols-1 gap-3 animate-fade-in-up">
            {eventData.options.map((option, index) => (
              <button
                key={index}
                onClick={() => onChoiceMade(option)}
                className="group relative w-full text-left bg-black/60 hover:bg-red-900/80 border border-gray-600 hover:border-red-400 p-4 rounded transition-all duration-300"
              >
                <span className="text-gray-400 group-hover:text-white font-mono mr-4">
                  0{index + 1}
                </span>
                <span className="text-gray-200 group-hover:text-white font-medium">
                  {option.text}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}